import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { createLead } from "./crmApi";

export const LEAD_STATUSES = [
  "new",
  "auto-response sent",
  "contacted",
  "demo scheduled",
  "demo attended",
  "interested",
  "enrolled",
  "lost",
];

export const MESSAGE_PROVIDERS = {
  whatsapp: "meta_whatsapp_cloud_api",
  email: "resend_or_brevo",
};

export const WHATSAPP_TEMPLATES = {
  demoAcknowledgement:
    "Hi {name}, thanks for requesting a free demo from QA Coder.\n\nWe received your request for career guidance in software testing. Our team will contact you shortly to understand your background and suggest the best course path.\n\n- QA Coder",
  demoFollowUp1:
    "Hi {name}, just following up on your demo request with QA Coder.\n\nWould you like:\n1. Job-switch guidance\n2. Automation testing roadmap\n3. Live demo session\n\nReply with 1, 2, or 3.",
  courseAcknowledgement:
    "Hi {name}, thanks for showing interest in QA Coder courses. Our team will review your requirement and share the right learning path shortly.\n\n- QA Coder",
};

const EMAIL_TEMPLATES = {
  newsletterWelcome: {
    subject: "Welcome to QA Coder",
    text: ({ firstName }) =>
      `Hi ${firstName}, thanks for subscribing to QA Coder. We will send practical QA roadmap, free learning resources, course links, and demo class updates.`,
    html: ({ firstName }) => `
      <p>Hi ${firstName},</p>
      <p>Thanks for subscribing to <strong>QA Coder</strong>.</p>
      <p>You will receive practical QA roadmap guidance, free learning resources, course recommendations, and demo class updates.</p>
      <p><strong>Start here:</strong> Explore Manual Testing, Automation Testing, API Testing, and Interview Prep from QA Coder.</p>
      <p>Regards,<br/>QA Coder</p>
    `,
  },
  newsletterDay1: {
    subject: "Recommended QA learning paths from QA Coder",
    text: ({ firstName }) =>
      `Hi ${firstName}, here are recommended QA paths: Manual Testing Foundation, Automation QA Path, API Testing, and Complete QA Career Program. Book a free demo when you are ready.`,
    html: ({ firstName }) => `
      <p>Hi ${firstName},</p>
      <p>Here are the best QA learning paths to explore next:</p>
      <ul>
        <li>Manual Testing Foundation</li>
        <li>Automation QA Path</li>
        <li>API Testing with Postman and Rest Assured</li>
        <li>Complete QA Career Program</li>
      </ul>
      <p>When you are ready, book a free demo and we will suggest the best path for your background.</p>
      <p>Regards,<br/>QA Coder</p>
    `,
  },
  newsletterDay3: {
    subject: "Still planning your QA career path?",
    text: ({ firstName }) =>
      `Hi ${firstName}, compare QA Coder course options and request a free demo if you want guidance choosing between Manual, Automation, API, or Complete QA.`,
    html: ({ firstName }) => `
      <p>Hi ${firstName},</p>
      <p>If you are still planning your QA career path, compare Manual Testing, Automation Testing, API Testing, and Complete QA programs.</p>
      <p>A free demo can help you choose the right path based on your current level.</p>
      <p>Regards,<br/>QA Coder</p>
    `,
  },
  demoAcknowledgement: {
    subject: "Your free demo request is received | QA Coder",
    text: ({ firstName, experienceLevel }) =>
      `Hi ${firstName}, thank you for requesting a free demo with QA Coder. We saw that you are ${experienceLevel || "looking for QA guidance"} and need guidance on the right course path. Our team will connect with you shortly and help you choose the best option based on your experience and goals.`,
    html: ({ firstName, experienceLevel }) => `
      <p>Hi ${firstName},</p>
      <p>Thank you for requesting a free demo with <strong>QA Coder</strong>.</p>
      <p>We saw that you are <strong>${experienceLevel || "looking for QA guidance"}</strong> and need guidance on the right course path. Our team will connect with you shortly and help you choose the best option based on your experience and goals.</p>
      <p><strong>What you can expect:</strong></p>
      <ul>
        <li>Course guidance</li>
        <li>Career roadmap</li>
        <li>Live demo session</li>
        <li>Doubt clearing</li>
      </ul>
      <p>Regards,<br/>QA Coder</p>
    `,
  },
  demoDay2: {
    subject: "Still interested in a QA demo session?",
    text: ({ firstName }) =>
      `Hi ${firstName}, checking if you are still interested in a QA Coder demo session. Submit your preferred time from the website and our team will guide you.`,
    html: ({ firstName }) => `
      <p>Hi ${firstName},</p>
      <p>Checking if you are still interested in a QA Coder demo session.</p>
      <p>You can request your preferred timing from the website and our team will guide you on the right QA path.</p>
      <p>Regards,<br/>QA Coder</p>
    `,
  },
  courseAcknowledgement: {
    subject: "QA Coder course enquiry received",
    text: ({ firstName, courseInterest }) =>
      `Hi ${firstName}, thanks for your interest in ${courseInterest || "QA Coder courses"}. Our team will review your requirement and suggest the best course path.`,
    html: ({ firstName, courseInterest }) => `
      <p>Hi ${firstName},</p>
      <p>Thanks for your interest in <strong>${courseInterest || "QA Coder courses"}</strong>.</p>
      <p>Our team will review your requirement and suggest the best course path.</p>
      <p>Regards,<br/>QA Coder</p>
    `,
  },
};

function getSourceContext(source) {
  return {
    source,
    sourcePath: window.location.hash || window.location.pathname,
    referrer: document.referrer || "direct",
    landingPage: sessionStorage.getItem("qaCoderFirstLandingPage") || window.location.href,
    userAgent: navigator.userAgent,
  };
}

function buildCrmLeadPayload(enquiry, source) {
  return {
    name: enquiry.name || "Website Visitor",
    email: enquiry.email || "",
    phone: enquiry.phone || "",
    enquiryType: enquiry.enquiryType || "general",
    course: enquiry.course || "",
    courseInterest: enquiry.courseInterest || "",
    experienceLevel: enquiry.experienceLevel || "",
    mode: enquiry.mode || "",
    message: enquiry.message || enquiry.subject || "",
    source: enquiry.source || source,
    sourcePath: enquiry.sourcePath || "",
    landingPage: enquiry.landingPage || "",
    referrer: enquiry.referrer || "",
  };
}

async function syncCrmLead(enquiry, source) {
  return createLead(buildCrmLeadPayload(enquiry, source));
}

function applyTemplate(template, values) {
  return template.replaceAll("{name}", values.firstName || "there");
}

function getScheduledDate(daysFromNow = 0) {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date;
}

function buildEmailAction({ enquiry, templateKey, sequenceStep, scheduledInDays = 0 }) {
  const firstName = getFirstName(enquiry.name);
  const template = EMAIL_TEMPLATES[templateKey];

  return {
    to: enquiry.email,
    provider: MESSAGE_PROVIDERS.email,
    templateKey,
    sequenceStep,
    status: enquiry.email ? "queued" : "missing_email",
    scheduledFor: getScheduledDate(scheduledInDays),
    message: {
      subject: template.subject,
      text: template.text({
        firstName,
        experienceLevel: enquiry.experienceLevel,
        courseInterest: enquiry.courseInterest,
      }),
      html: template.html({
        firstName,
        experienceLevel: enquiry.experienceLevel,
        courseInterest: enquiry.courseInterest,
      }),
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

function getFirstName(name) {
  return name?.trim()?.split(/\s+/)[0] || "there";
}

export function buildDemoWhatsAppMessage(name) {
  return applyTemplate(WHATSAPP_TEMPLATES.demoAcknowledgement, { firstName: getFirstName(name) });
}

export function buildWhatsAppMessage(templateKey, name) {
  return applyTemplate(WHATSAPP_TEMPLATES[templateKey] || WHATSAPP_TEMPLATES.demoAcknowledgement, {
    firstName: getFirstName(name),
  });
}

export function normalizeWhatsAppPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");

  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;

  return digits;
}

export function getWhatsAppDemoLink({ phone, name }) {
  const normalizedPhone = normalizeWhatsAppPhone(phone);

  if (!normalizedPhone) return "";

  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(buildDemoWhatsAppMessage(name))}`;
}

function getInitialWorkflow(enquiryType) {
  const isDemo = enquiryType === "demo";

  return {
    status: "new",
    nextStatus: isDemo || enquiryType === "course" ? "auto-response sent" : "auto-response sent",
    availableStatuses: LEAD_STATUSES,
    contactedAt: null,
    autoResponseSentAt: null,
    demoScheduledAt: null,
    demoAttendedAt: null,
    enrolledAt: null,
    lostAt: null,
    history: [
      {
        status: "new",
        note: isDemo ? "Demo request received from website." : "Enquiry received from website.",
        createdAt: new Date().toISOString(),
      },
    ],
    lastUpdatedAt: serverTimestamp(),
  };
}

function getInitialAutomation(enquiryType) {
  return {
    providerPlan: {
      whatsapp: MESSAGE_PROVIDERS.whatsapp,
      email: MESSAGE_PROVIDERS.email,
      emailOptions: ["resend", "brevo"],
    },
    welcomeEmailSent: false,
    welcomeWhatsAppSent: false,
    followUp1Sent: false,
    followUp2Sent: false,
    lastMessageChannel: null,
    lastMessageAt: null,
    activeSequence: enquiryType,
  };
}

function buildWhatsAppAction({ enquiry, enquiryRefId, templateKey, sequenceStep, scheduledInDays = 0 }) {
  const whatsappMessage = buildWhatsAppMessage(templateKey, enquiry.name);
  const normalizedPhone = normalizeWhatsAppPhone(enquiry.phone);

  return {
    enquiryId: enquiryRefId,
    enquiryType: enquiry.enquiryType,
    provider: MESSAGE_PROVIDERS.whatsapp,
    requiresApprovedTemplate: true,
    templateKey,
    sequenceStep,
    status: normalizedPhone ? "queued" : "missing_phone",
    phone: normalizedPhone,
    recipientName: enquiry.name || "",
    courseInterest: enquiry.courseInterest || "",
    message: whatsappMessage,
    link: normalizedPhone
      ? `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(whatsappMessage)}`
      : "",
    scheduledFor: getScheduledDate(scheduledInDays),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

function getLeadAutomationPlan({ enquiry, enquiryRefId, source }) {
  const baseAdminMessage = `${enquiry.name || "A visitor"} submitted a ${enquiry.enquiryType} enquiry.`;

  if (enquiry.enquiryType === "newsletter") {
    return {
      adminMessage: `${enquiry.name || "A visitor"} subscribed to QA Coder newsletter.`,
      nextAction: "Nurture with email sequence",
      emailActions: [
        buildEmailAction({ enquiry, templateKey: "newsletterWelcome", sequenceStep: "instant", scheduledInDays: 0 }),
        buildEmailAction({ enquiry, templateKey: "newsletterDay1", sequenceStep: "day_1", scheduledInDays: 1 }),
        buildEmailAction({ enquiry, templateKey: "newsletterDay3", sequenceStep: "day_3", scheduledInDays: 3 }),
      ],
      whatsappActions: [],
      leadTasks: [
        {
          enquiryId: enquiryRefId,
          title: "Review newsletter subscriber",
          description: "Check if the subscriber later requests a demo or course consultation.",
          status: "open",
          dueAt: getScheduledDate(3),
          source,
        },
      ],
    };
  }

  if (enquiry.enquiryType === "demo") {
    return {
      adminMessage: `${enquiry.name || "A visitor"} requested a demo for ${enquiry.courseInterest || "QA training"}.`,
      nextAction: "Send acknowledgement and contact within 24 hours",
      emailActions: [
        buildEmailAction({ enquiry, templateKey: "demoAcknowledgement", sequenceStep: "instant", scheduledInDays: 0 }),
        buildEmailAction({ enquiry, templateKey: "demoDay2", sequenceStep: "day_2", scheduledInDays: 2 }),
      ],
      whatsappActions: [
        buildWhatsAppAction({ enquiry, enquiryRefId, templateKey: "demoAcknowledgement", sequenceStep: "instant", scheduledInDays: 0 }),
        buildWhatsAppAction({ enquiry, enquiryRefId, templateKey: "demoFollowUp1", sequenceStep: "day_1", scheduledInDays: 1 }),
      ],
      leadTasks: [
        {
          enquiryId: enquiryRefId,
          title: "Contact demo lead",
          description: "Call or message the learner, understand background, and schedule a demo.",
          status: "open",
          dueAt: getScheduledDate(1),
          source,
        },
      ],
    };
  }

  if (enquiry.enquiryType === "course") {
    return {
      adminMessage: `${enquiry.name || "A visitor"} asked about ${enquiry.courseInterest || "a QA Coder course"}.`,
      nextAction: "Counselor follow-up required",
      emailActions: [
        buildEmailAction({ enquiry, templateKey: "courseAcknowledgement", sequenceStep: "instant", scheduledInDays: 0 }),
      ],
      whatsappActions: [
        buildWhatsAppAction({ enquiry, enquiryRefId, templateKey: "courseAcknowledgement", sequenceStep: "instant", scheduledInDays: 0 }),
      ],
      leadTasks: [
        {
          enquiryId: enquiryRefId,
          title: "Course counselor follow-up",
          description: "Review course interest and suggest the best course path.",
          status: "open",
          dueAt: getScheduledDate(1),
          source,
        },
      ],
    };
  }

  return {
    adminMessage: baseAdminMessage,
    nextAction: "Review enquiry",
    emailActions: enquiry.email
      ? [buildEmailAction({ enquiry, templateKey: "courseAcknowledgement", sequenceStep: "instant", scheduledInDays: 0 })]
      : [],
    whatsappActions: [],
    leadTasks: [],
  };
}

async function tryAddDoc(collectionName, payload) {
  try {
    return await addDoc(collection(db, collectionName), payload);
  } catch (error) {
    console.warn(`Optional ${collectionName} write skipped:`, error);
    return null;
  }
}

export async function createEnquiry(payload, options = {}) {
  const enquiryType = options.enquiryType || payload.enquiryType || "contact";
  const source = options.source || payload.source || "website";

  let enquiry = {
    ...payload,
    ...getSourceContext(source),
    enquiryType,
    leadStatus: "new",
    workflow: getInitialWorkflow(enquiryType),
    automation: getInitialAutomation(enquiryType),
    tags: Array.from(
      new Set([
        enquiryType,
        source,
        ...(enquiryType === "newsletter" ? ["cold", "nurture"] : []),
        ...(enquiryType === "demo" ? ["hot", "demo"] : []),
        ...(payload.courseInterest ? ["course"] : []),
        ...(payload.subject?.toLowerCase().includes("support") ? ["support"] : []),
      ])
    ),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const crmLead = await syncCrmLead(enquiry, source);

  enquiry = {
    ...enquiry,
    crmLeadId: crmLead?.id || null,
  };

  const enquiryRef = await addDoc(collection(db, "enquiries"), enquiry);
  const automationPlan = getLeadAutomationPlan({ enquiry, enquiryRefId: enquiryRef.id, source });

  await tryAddDoc("admin_notifications", {
    type: enquiryType === "demo" ? "demo_enquiry" : "new_enquiry",
    title: enquiryType === "demo" ? "New demo request received" : "New enquiry received",
    message: automationPlan.adminMessage,
    enquiryId: enquiryRef.id,
    leadStatus: "new",
    nextAction: automationPlan.nextAction,
    actionRequired: ["demo", "course"].includes(enquiryType),
    read: false,
    createdAt: serverTimestamp(),
  });

  for (const emailAction of automationPlan.emailActions) {
    await tryAddDoc("email_actions", {
      ...emailAction,
      enquiryId: enquiryRef.id,
      enquiryType,
      leadStatus: "new",
      source,
    });
  }

  for (const whatsappAction of automationPlan.whatsappActions) {
    await tryAddDoc("whatsapp_actions", {
      ...whatsappAction,
      leadStatus: "new",
      source,
    });
  }

  for (const leadTask of automationPlan.leadTasks) {
    await tryAddDoc("lead_tasks", {
      ...leadTask,
      enquiryType,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  return enquiryRef;
}

export async function getEnquiriesSummary() {
  const enquiriesSnapshot = await getDocs(collection(db, "enquiries"));
  const enquiries = enquiriesSnapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .sort((first, second) => {
      const firstDate = typeof first.createdAt?.toDate === "function" ? first.createdAt.toDate() : new Date(first.createdAt || 0);
      const secondDate = typeof second.createdAt?.toDate === "function" ? second.createdAt.toDate() : new Date(second.createdAt || 0);
      return secondDate - firstDate;
    });

  return {
    enquiries,
    totalEnquiries: enquiries.length,
  };
}

export async function updateEnquiryLeadStatus(enquiryId, nextStatus) {
  const normalizedStatus = LEAD_STATUSES.includes(nextStatus) ? nextStatus : "new";
  const timestampFieldMap = {
    "auto-response sent": "autoResponseSentAt",
    contacted: "contactedAt",
    "demo scheduled": "demoScheduledAt",
    "demo attended": "demoAttendedAt",
    enrolled: "enrolledAt",
    lost: "lostAt",
  };
  const timestampField = timestampFieldMap[normalizedStatus];

  await updateDoc(doc(db, "enquiries", enquiryId), {
    leadStatus: normalizedStatus,
    "workflow.status": normalizedStatus,
    "workflow.lastUpdatedAt": serverTimestamp(),
    ...(timestampField ? { [`workflow.${timestampField}`]: serverTimestamp() } : {}),
    updatedAt: serverTimestamp(),
  });
}

export async function getEnquiriesSince(date) {
  const enquiriesQuery = query(collection(db, "enquiries"), where("createdAt", ">=", date));
  const snapshot = await getDocs(enquiriesQuery);
  return snapshot.size;
}
